import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, Sub, Table } from '../../performance-operations/styles';
import { BANDS, matrixCell } from '../config';

// Analiz §14 E11 orta sağ — bant değişimi için geçiş matrisi (ilk → son dönem; yüksek/orta/düşük). Kişi düzeyinde eşleşme ister:
// her zaman ortak kohorttan hesaplanır. Küçük hücre ve tamamlayıcısı gizlidir (kilit ikonu; değer hiçbir yolla açılmaz); bir
// hücre gizliyse hareket özeti (yukarı/aynı/aşağı) de gösterilmez. Hücre → çalışan listesi (kişi kümenizle sınırlı).
const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(70px, auto) repeat(3, minmax(0, 1fr));
  gap: 6px;
  align-items: stretch;
`;

const Head = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.muted};
  display: flex;
  align-items: center;
  justify-content: ${({ $left }) => ($left ? 'flex-start' : 'center')};
`;

const CellBox = styled.div`
  border-radius: 8px;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $diag }) => ($diag ? theme.colors.bgColor : theme.colors.surface.card)};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme, $tone }) => ($tone === 'up' ? theme.colors.secondary.dark : $tone === 'down' ? theme.colors.auxiliar.danger : theme.colors.text.primary)};
`;

const Movement = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const rank = (b) => ({ high: 2, mid: 1, low: 0 }[b]);
const tone = (from, to) => (rank(to) > rank(from) ? 'up' : rank(to) < rank(from) ? 'down' : 'same');

export default function TransitionPanel({ panel, onCell }) {
  const { t } = useTranslation();
  const [table, setTable] = useState(false);
  const m = panel.data;
  const band = (b) => t(`performance.trends.band.${b}`);
  const empty = !m || (m.n === 0 && !m.suppressionReason);
  const label = (from, to) => t('performance.trends.transition.cellLabel', { from: band(from), to: band(to) });

  const cellContent = (c) => (c?.suppressed
    ? <Lock size={14} role="img" aria-label={t('performance.trends.suppressed')} />
    : (c?.count ?? 0));

  return (
    <PanelCard
      panelKey="transitions" title={t('performance.trends.panel.transitions')} span={5}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={empty}
      emptyText={t('performance.trends.transition.empty')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {m?.suppressionReason ? <Sub data-state="suppressed">{t('performance.trends.transition.suppressedAll')}</Sub> : null}
      {m && !m.suppressionReason ? (
        <>
          {table ? (
            <Table>
              <thead>
                <tr>
                  <th>{t('performance.trends.transition.from', { name: m.from?.name || '' })}</th>
                  <th>{t('performance.trends.transition.to', { name: m.to?.name || '' })}</th>
                  <th>{t('performance.trends.col.n')}</th>
                </tr>
              </thead>
              <tbody>
                {BANDS.flatMap((f) => BANDS.map((to) => {
                  const c = matrixCell(m, f, to);
                  return (
                    <tr key={`${f}>${to}`} data-row={`${f}>${to}`}>
                      <td>{band(f)}</td>
                      <td>{band(to)}</td>
                      <td>
                        {c?.suppressed ? t('performance.trends.suppressed') : (
                          <Clickable onClick={() => onCell?.(f, to)} aria-label={label(f, to)}>{c?.count ?? 0}</Clickable>
                        )}
                      </td>
                    </tr>
                  );
                }))}
              </tbody>
            </Table>
          ) : (
            <Grid role="grid" aria-label={t('performance.trends.panel.transitions')}>
              <Head $left>{t('performance.trends.transition.fromTo')}</Head>
              {BANDS.map((to) => <Head key={`h-${to}`}>{band(to)}</Head>)}
              {BANDS.map((f) => (
                <React.Fragment key={`r-${f}`}>
                  <Head $left>{band(f)}</Head>
                  {BANDS.map((to) => {
                    const c = matrixCell(m, f, to);
                    const box = <CellBox $diag={f === to} $tone={tone(f, to)} data-cell={`${f}>${to}`}>{cellContent(c)}</CellBox>;
                    return c?.suppressed || !c?.count
                      ? <div key={`${f}>${to}`} title={c?.suppressed ? t('performance.trends.suppressed') : undefined}>{box}</div>
                      : <Clickable key={`${f}>${to}`} onClick={() => onCell?.(f, to)} aria-label={label(f, to)}>{box}</Clickable>;
                  })}
                </React.Fragment>
              ))}
            </Grid>
          )}
          <Movement data-movement="1">
            {m.up === null || m.up === undefined
              ? <Sub>{t('performance.trends.transition.movementHidden')}</Sub>
              : (
                <>
                  <span data-move="up">{t('performance.trends.transition.up', { count: m.up })}</span>
                  <span data-move="same">{t('performance.trends.transition.same', { count: m.same })}</span>
                  <span data-move="down">{t('performance.trends.transition.down', { count: m.down })}</span>
                </>
              )}
          </Movement>
          <Sub style={{ marginTop: 6 }}>{t('performance.trends.transition.note', { n: m.n ?? 0 })}</Sub>
        </>
      ) : null}
    </PanelCard>
  );
}
